import { useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { PolicySectionsResponse } from "../policySections/PolicySections.types";
import { CompareResult, DiffStatus, PolicyCompareResponse, PolicyVersionSummary } from "./PolicyCompareVersions.types";

export function policyVersionsQueryKey(policyId: string) {
    return `policy-versions-${policyId}`;
}

export function usePolicyVersionsQuery(policyId: string) {
    return useDotLegalQueryWithKey<PolicyVersionSummary[]>(policyVersionsQueryKey(policyId), `/policies/${policyId}/versions`);
}

export function usePolicyVersionSectionsQuery(policyId: string, versionId: string | undefined) {
    return useDotLegalQueryWithKey<PolicySectionsResponse>(
        `policy-version-sections-${policyId}-${versionId}`,
        `/policies/${policyId}/versions/${versionId}/sections`,
        {
            enabled: !!versionId,
        }
    );
}

// Backend PolicyDiffStatus int → DiffStatus string union, indexed by enum value.
const diffStatusByCode: DiffStatus[] = ["unchanged", "added", "removed", "modified"];

function mapCompareResult(response: PolicyCompareResponse): CompareResult {
    return {
        summary: response.summary,
        sectionPairs: response.sectionPairs.map((pair) => ({
            originalSectionId: pair.originalSectionId,
            status: diffStatusByCode[pair.status],
            reordered: pair.reordered,
            fromSection: pair.fromSection,
            toSection: pair.toSection,
            titleDiff: pair.titleDiff,
            contentDiff: pair.contentDiff,
            subsectionPairs: pair.subsectionPairs.map((sub) => ({
                originalSubsectionId: sub.originalSubsectionId,
                status: diffStatusByCode[sub.status],
                reordered: sub.reordered,
                fromSubsection: sub.fromSubsection,
                toSubsection: sub.toSubsection,
                titleDiff: sub.titleDiff,
                contentDiff: sub.contentDiff,
            })),
        })),
    };
}

export function usePolicyVersionComparison(policyId: string, fromVersionId: string | undefined, toVersionId: string | undefined) {
    const query = useDotLegalQueryWithKey<PolicyCompareResponse>(
        `policy-compare-${policyId}-${fromVersionId}-${toVersionId}`,
        `/policies/${policyId}/compare?fromVersionId=${fromVersionId}&toVersionId=${toVersionId}`,
        { enabled: !!fromVersionId && !!toVersionId }
    );

    return { compareResult: query.data ? mapCompareResult(query.data) : null, isLoading: query.isLoading };
}
